import React, { useState, useRef, useEffect } from 'react';
import { Search, ChevronDown, X } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

/**
 * Searchable single-select dropdown.
 *
 * Props:
 * - value: selected option value
 * - onChange: (value) => void
 * - options: array of { value, label, color?, render? }
 * - placeholder: string
 * - className: wrapper class
 * - triggerClassName: trigger button class
 * - disabled: boolean
 */
export default function SearchableSelect({
    value,
    onChange,
    options = [],
    placeholder = 'Select...',
    className,
    triggerClassName,
    disabled = false,
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectedOption = options.find(o => o.value === value);

    const filteredOptions = options.filter(o =>
        o.label?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSelect = (val) => {
        onChange(val);
        setIsOpen(false);
        setSearchTerm('');
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange('');
        setSearchTerm('');
    };

    return (
        <div className={cn('relative', className)} ref={containerRef}>
            <Button
                variant="outline"
                size="sm"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                className={cn(
                    'h-9 w-full justify-between text-left font-normal bg-white',
                    disabled && 'opacity-50 cursor-not-allowed',
                    triggerClassName
                )}
                type="button"
            >
                <div className="flex items-center gap-2 truncate">
                    {selectedOption?.color && (
                        <div
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ backgroundColor: selectedOption.color }}
                        />
                    )}
                    <span className={cn('truncate', !selectedOption && 'text-brand-text-muted')}>
                        {selectedOption?.label || placeholder}
                    </span>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                    {value && (
                        <span
                            onClick={handleClear}
                            className="p-0.5 rounded-full hover:bg-brand-accent-soft text-brand-text-muted transition-colors"
                            role="button"
                            aria-label="Clear selection"
                        >
                            <X className="w-3 h-3" />
                        </span>
                    )}
                    <ChevronDown className="w-3.5 h-3.5 text-brand-text-muted" />
                </div>
            </Button>

            {isOpen && (
                <div className="absolute top-full left-0 mt-1 w-full min-w-[200px] bg-white border border-brand-border rounded-lg shadow-lg z-50 overflow-hidden">
                    {/* Search input */}
                    <div className="p-2 border-b border-brand-border">
                        <div className="relative">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-brand-text-muted" />
                            <Input
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Cari..."
                                className="h-8 pl-7 text-sm"
                                autoFocus
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    </div>

                    {/* Options list */}
                    <div className="max-h-56 overflow-y-auto">
                        {filteredOptions.length === 0 ? (
                            <div className="px-3 py-4 text-sm text-brand-text-muted text-center">
                                Tidak ditemukan
                            </div>
                        ) : (
                            filteredOptions.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => handleSelect(option.value)}
                                    className={cn(
                                        'w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-brand-accent-soft/50 transition-colors',
                                        option.value === value && 'bg-brand-accent-soft'
                                    )}
                                >
                                    {option.color && (
                                        <div
                                            className="w-2 h-2 rounded-full flex-shrink-0"
                                            style={{ backgroundColor: option.color }}
                                        />
                                    )}
                                    <span className="truncate">{option.label}</span>
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
